import React, {
  useState,
  ReactNode,
  useMemo,
  useRef,
  useCallback,
  useEffect,
} from "react";
import { IconButton, Menu, MenuItem, Typography, Box } from "@mui/material";
import {
  MoreVert as MoreVertIcon,
  Fullscreen as FullscreenIcon,
  Download as DownloadIcon,
  ZoomOutMap as ZoomOutMapIcon,
} from "@mui/icons-material";
import Plotly from "plotly.js-dist-min";
import PlotModal from "./PlotModal";

export interface BasePlotProps {
  title: string;
  children: ReactNode;
  className?: string;
  showColorPicker?: boolean;
  defaultColor?: string;
  onColorChange?: (color: string) => void;
  showSizeSlider?: boolean;
  defaultSize?: number;
  onSizeChange?: (size: number) => void;
  downloadFilename?: string;
  plotRef?: string; // CSS selector for the plot element
  isFullscreen?: boolean; // Whether the plot is being rendered in fullscreen modal
  // New props for modal rendering
  plotType?: "violin" | "scatter" | "histogram" | "bar" | "dot" | "custom";
  plotProps?: {
    data?: any[];
    metric?: string;
    xMetric?: string;
    yMetric?: string;
    sizeMetric?: string;
    colorMetric?: string;
    defaultBins?: number;
    [key: string]: any;
  };
}

const BasePlot = ({
  title,
  children,
  className = "",
  showColorPicker = false,
  defaultColor = "#19C1DB",
  onColorChange,
  showSizeSlider = false,
  defaultSize = 8,
  onSizeChange,
  downloadFilename = "plot",
  plotRef,
  isFullscreen = false,
  plotType = "custom",
  plotProps = {},
}: BasePlotProps): React.ReactElement => {
  const [color, setColor] = useState(defaultColor);
  const [size, setSize] = useState(defaultSize);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  // The current container that holds the .plotly-plot div
  const plotElementRef = useRef<HTMLElement | null>(null);

  // Resize observer + bookkeeping
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const resizeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSizeRef = useRef({ width: 0, height: 0 });

  // Generate a unique plot reference if none provided
  const uniquePlotRef = useMemo(() => {
    return plotRef || `#plot-${Math.random().toString(36).substr(2, 9)}`;
  }, [plotRef]);

  const handleResize = useCallback(
    (container: HTMLElement) => {
      const rect = container.getBoundingClientRect();
      const { width, height } = rect;

      if (
        Math.abs(width - lastSizeRef.current.width) <= 2 &&
        Math.abs(height - lastSizeRef.current.height) <= 2
      ) {
        return;
      }

      lastSizeRef.current = { width, height };

      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }

      resizeTimeoutRef.current = setTimeout(() => {
        const plotDiv = container.querySelector(".plotly-plot") as any;
        if (plotDiv && Plotly) {
          const relayoutOptions: any = {
            autosize: true,
            width: Math.max(width, 240),
            height: Math.max(height, 240),
            "xaxis.autorange": true,
            "yaxis.autorange": true,
          };

          // only force height in non-fullscreen mode (dashboard)
          // if (!isFullScreen) {
          //   relayoutOptions.height = minHeight;
          // }
          Plotly.relayout(plotDiv, relayoutOptions);
        }
      }, 50);
    },
    []
  );

  // Callback to set the plot element ref AND manage the ResizeObserver
  const setPlotElementRef = useCallback(
    (element: HTMLElement | null) => {
      // Detach from old element
      if (plotElementRef.current && resizeObserverRef.current) {
        resizeObserverRef.current.unobserve(plotElementRef.current);
      }

      plotElementRef.current = element;

      if (!element) {
        return;
      }

      // Lazily create observer
      if (!resizeObserverRef.current) {
        resizeObserverRef.current = new ResizeObserver((entries) => {
          for (const entry of entries) {
            const target = entry.target as HTMLElement;
            handleResize(target);
          }
        });
      }

      // Attach to new element
      resizeObserverRef.current.observe(element);

      // Immediately do one resize to ensure correct size (important on reopen)
      handleResize(element);
    },
    [handleResize]
  );

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      if (resizeObserverRef.current && plotElementRef.current) {
        resizeObserverRef.current.unobserve(plotElementRef.current);
      }
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, []);

  const handleColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newColor = e.target.value;
    setColor(newColor);
    onColorChange?.(newColor);
  };

  const handleSizeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSize = Number(e.target.value);
    setSize(newSize);
    onSizeChange?.(newSize);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleFullscreenToggle = () => {
    setIsFullScreen((prev) => !prev);
    handleMenuClose();
  };

  const downloadPlot = (format: "png" | "svg" = "png") => {
    const plotElement =
      (plotElementRef.current?.querySelector(".plotly-plot") as any) ||
      (document.querySelector(uniquePlotRef) as any);
    if (plotElement) {
      Plotly.downloadImage(plotElement, {
        format,
        width: 1000,
        height: 1000,
        filename: `${downloadFilename}_${format}`,
      });
    }
    handleMenuClose();
  };

  const savePlotAsPdf = async () => {
    const plotElement =
      (plotElementRef.current?.querySelector(".plotly-plot") as any) ||
      (document.querySelector(uniquePlotRef) as any);
    if (!plotElement) return;

    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      window.alert("Allow pop-ups to save this plot as PDF.");
      return;
    }

    try {
      const imageUrl = await Plotly.toImage(plotElement, { format: "png", width: 1400, height: 1400 });
      printWindow.document.write(
        "<html><head><title>" + downloadFilename + "</title></head>" +
        "<body style=\"margin:0;display:flex;justify-content:center;align-items:center\">" +
        "<img id=\"plot-image\" style=\"width:95vw;height:95vh;object-fit:contain\" src=\"" + imageUrl + "\">" +
        "</body></html>",
      );
      printWindow.document.close();
      const image = printWindow.document.getElementById("plot-image") as HTMLImageElement | null;
      const print = () => { printWindow.focus(); printWindow.print(); };
      if (image?.complete) print();
      else if (image) image.onload = print;
    } catch (error) {
      printWindow.close();
      console.error("PDF export failed:", error);
      window.alert("Could not prepare this plot for PDF export.");
    }
    handleMenuClose();
  };

  const resetZoom = () => {
    const plotElement =
      (plotElementRef.current?.querySelector(".plotly-plot") as any) ||
      (document.querySelector(uniquePlotRef) as any);
    if (plotElement) {
      Plotly.relayout(plotElement, {
        "xaxis.autorange": true,
        "yaxis.autorange": true,
      });
    }
    handleMenuClose();
  };

  const fullscreenRendering = isFullscreen || isFullScreen;

  // Render function — creates a fresh Box each time and wires the ref
  const renderPlot = () => (
    <Box
      key={fullscreenRendering ? "plot-fullscreen" : "plot-normal"}
      className="w-full h-full"
      ref={setPlotElementRef}
      sx={
        fullscreenRendering
          ? { height: "100%", minHeight: 0, display: "flex", flex: 1, flexDirection: "column" }
          : { height: "100%", minHeight: 0, display: "flex", flexDirection: "column" }
      }
    >
      {children}
    </Box>
  );

  // Extra: when entering fullscreen, do another explicit resize after dialog animation
  useEffect(() => {
    if (!isFullScreen) return;
    const timer = setTimeout(() => {
      if (plotElementRef.current) {
        handleResize(plotElementRef.current);
      }
    }, 200); // give Modal some time to layout

    return () => clearTimeout(timer);
  }, [isFullScreen, handleResize]);

  return (
    <div
      className={`flex flex-col min-h-0 w-full relative ${className}`}
      style={{
        overflow: "hidden",
        aspectRatio: isFullscreen ? undefined : "1 / 1",
        height: isFullscreen ? "100%" : "auto",
        maxWidth: "100%",
        alignSelf: "stretch",
      }}
    >
      {/* Header */}
      <div className="flex justify-between items-center mb-2">
        <Typography
          variant="h6"
          className="text-gray-800 font-semibold"
          sx={{ fontSize: "0.875rem", fontWeight: 600 }}
        >
          {title}
        </Typography>

        <div className="flex items-center gap-2">
          {showColorPicker && (
            <input
              type="color"
              value={color}
              onChange={handleColorChange}
              className="w-8 h-8 border-none bg-transparent cursor-pointer p-0 rounded"
              title="Change color"
            />
          )}

          {showSizeSlider && (
            <input
              type="range"
              min="3"
              max="15"
              step="1"
              value={size}
              onChange={handleSizeChange}
              title={`Size: ${size}`}
              className="w-20"
            />
          )}

          <IconButton
            size="small"
            onClick={handleMenuClick}
            className="text-gray-600 hover:text-gray-800"
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </div>
      </div>

      {/* Plot content (only when NOT fullscreen) */}
      <div
        className="flex-1 min-h-0"
        style={{
          minWidth: "200px",
          minHeight: "320px",
          height: "100%",
          overflow: "hidden",
        }}
      >
        {!isFullScreen && renderPlot()}
      </div>

      {/* Menu */}
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={resetZoom}>
          <ZoomOutMapIcon fontSize="small" sx={{ mr: 1 }} />
          Reset Zoom
        </MenuItem>
        {!isFullscreen && (
          <MenuItem onClick={handleFullscreenToggle}>
            <FullscreenIcon fontSize="small" sx={{ mr: 1 }} />
            Fullscreen
          </MenuItem>
        )}
        <MenuItem onClick={() => downloadPlot("png")}>
          <DownloadIcon fontSize="small" sx={{ mr: 1 }} />
          Download PNG
        </MenuItem>
        <MenuItem onClick={() => downloadPlot("svg")}>
          <DownloadIcon fontSize="small" sx={{ mr: 1 }} />
          Download SVG
        </MenuItem>
        <MenuItem onClick={savePlotAsPdf}>
          <DownloadIcon fontSize="small" sx={{ mr: 1 }} />
          Save as PDF
        </MenuItem>
      </Menu>

      {/* Fullscreen Modal — plot lives HERE when fullscreen */}
      <PlotModal
        open={isFullScreen}
        onClose={handleFullscreenToggle}
        title={title}
        plotType={plotType}
        plotProps={plotProps}
        defaultColor={defaultColor}
        defaultSize={defaultSize}
      >
        {isFullScreen && renderPlot()}
      </PlotModal>
    </div>
  );
};

export default BasePlot;
